import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Link,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OrderProduct, ProductFieldOptionPayload } from '../../types/order';
import { ORDER_BAR_BG, ORDER_BAR_TEXT, ORDER_BAR_TEXT_MUTED } from './OrderPageHeader';

/** Config from Category Management → Product Fields (which fields to show on plan card). */
export interface ProductFieldConfigItem {
  field_key: string;
  display_order: number;
  /** Default label from Category Management (used when label_override is empty). */
  default_display_label?: string;
  label_override?: string;
  is_visible: boolean;
  field_type?: string;
  field_options?: ProductFieldOptionPayload[];
}

interface PlanCarouselProps {
  plans: OrderProduct[];
  selectedPlan: OrderProduct | null;
  onSelectPlan: (plan: OrderProduct) => void;
  subcategoryName?: string;
  /** When set, only these fields are shown on each plan card (from Product Fields tab). */
  productFieldsConfig?: ProductFieldConfigItem[];
  /** When set, the main CTA is an "Order" link to this URL (opens in new tab). Used for embed page. */
  getOrderLink?: (plan: OrderProduct) => string;
  /** Optional overrides for field labels (e.g. voice plans: { description: 'Inclusions' }). */
  fieldLabelOverrides?: Record<string, string>;
  /** When true, hides the "Select your plan" heading (used for the embed page). */
  hideHeading?: boolean;
}

const CARD_WIDTH = 320;
const SCROLL_GAP = 16;

/**
 * Parse newline- or comma-separated string into array of trimmed non-empty strings.
 * Used for features and for "Designed for" (description) list items.
 */
function parseListText(text?: string): string[] {
  if (!text || !text.trim()) return [];
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Horizontal scrolling plan carousel with left/right arrows (Aussie Broadband style).
 * Shows typical evening speed (7pm–11pm), description, and features. Selecting a plan updates the tally bar.
 */
const DEFAULT_FIELD_ORDER = [
  'name',
  'speed_tier',
  'data_cap',
  'monthly_price',
  'setup_price',
  'available_technology_types', // NBN: shown above typical evening speed
  'typical_evening_speed',
  'description',
  'features',
  'one_time_price',
  'contract_terms',
  'max_channels',
];

const DEFAULT_LABELS: Record<string, string> = {
  available_technology_types: 'Available for',
  typical_evening_speed: 'Typical evening speed 7pm – 11pm',
  description: 'Designed for',
  features: 'Features',
  one_time_price: 'One-time price',
  max_channels: 'Max channels',
};

/** Fields shown in the coloured header band at the top of each plan card (Sonic brand). */
const PLAN_CARD_HEADER_KEYS = ['name', 'speed_tier', 'data_cap', 'monthly_price', 'setup_price'];

function PlanCarousel({ plans, selectedPlan, onSelectPlan, subcategoryName, productFieldsConfig, getOrderLink, fieldLabelOverrides, hideHeading }: PlanCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // When a plan is preselected (e.g. via deep link), scroll the carousel so it is visible
  useEffect(() => {
    if (!selectedPlan || !scrollRef.current || plans.length === 0) return;
    const index = plans.findIndex((p) => p.id === selectedPlan.id);
    if (index < 0) return;
    const scrollLeft = index * (CARD_WIDTH + SCROLL_GAP);
    scrollRef.current.scrollLeft = scrollLeft;
  }, [selectedPlan?.id, plans]);

  const formatPrice = (price?: number) => {
    if (price == null) return 'N/A';
    return `$${Number(price).toFixed(2)}`;
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = (CARD_WIDTH + SCROLL_GAP) * (direction === 'left' ? -1 : 1);
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const fieldOrder =
    productFieldsConfig && productFieldsConfig.length > 0
      ? productFieldsConfig.map((f) => f.field_key)
      : DEFAULT_FIELD_ORDER;

  const headerFieldKeys = fieldOrder.filter((k) => PLAN_CARD_HEADER_KEYS.includes(k));
  const bodyFieldKeys = fieldOrder.filter((k) => !PLAN_CARD_HEADER_KEYS.includes(k));

  const getLabel = (fieldKey: string) => {
    const cfg = productFieldsConfig?.find((f) => f.field_key === fieldKey);
    return (
      cfg?.label_override ||
      cfg?.default_display_label ||
      fieldLabelOverrides?.[fieldKey] ||
      DEFAULT_LABELS[fieldKey] ||
      fieldKey.replace(/_/g, ' ')
    );
  };

  const jsonOr = (plan: OrderProduct, key: string): string | undefined => {
    const raw = plan.plan_features?.[key];
    if (raw !== undefined && raw !== null && String(raw).trim() !== '') return String(raw);
    return undefined;
  };

  const headerText = { color: ORDER_BAR_TEXT };
  const headerTextMuted = { color: ORDER_BAR_TEXT_MUTED };

  const renderField = (fieldKey: string, plan: OrderProduct, inHeader?: boolean) => {
    const label = getLabel(fieldKey);
    const j = jsonOr(plan, fieldKey);
    switch (fieldKey) {
      case 'name': {
        const display = j ?? plan.name;
        if (!display) return null;
        return (
          <Typography
            key="name"
            variant="subtitle1"
            fontWeight={700}
            gutterBottom
            sx={inHeader ? headerText : undefined}
          >
            {display}
          </Typography>
        );
      }
      case 'speed_tier': {
        const st = j ?? plan.speed_tier;
        const dc = jsonOr(plan, 'data_cap') ?? plan.data_cap;
        if (!st) return null;
        return (
          <Typography
            key="speed_tier"
            variant="body2"
            gutterBottom
            sx={inHeader ? headerTextMuted : { color: 'text.secondary' }}
          >
            {st}
            {dc ? ` · ${dc}` : ''}
          </Typography>
        );
      }
      case 'data_cap': {
        const st = jsonOr(plan, 'speed_tier') ?? plan.speed_tier;
        const dc = j ?? plan.data_cap;
        if (!dc || st) return null;
        return (
          <Typography
            key="data_cap"
            variant="body2"
            gutterBottom
            sx={inHeader ? headerTextMuted : { color: 'text.secondary' }}
          >
            {dc}
          </Typography>
        );
      }
      case 'monthly_price': {
        const js = j;
        const price = js != null && js.trim() !== '' ? parseFloat(js) : plan.monthly_price;
        return (
          <Typography
            key="monthly_price"
            variant="body1"
            fontWeight={600}
            sx={{
              mb: 1,
              ...(inHeader ? headerText : { color: 'primary.main' }),
              ...(inHeader ? { fontSize: '1.125rem' } : {}),
            }}
          >
            {formatPrice(price)} / month
          </Typography>
        );
      }
      case 'setup_price': {
        const js = j;
        const raw = js != null && js.trim() !== '' ? parseFloat(js) : plan.setup_price;
        if (raw == null || Number(raw) <= 0) return null;
        return (
          <Typography
            key="setup_price"
            variant="body2"
            sx={inHeader ? { ...headerTextMuted, mb: 1 } : { color: 'text.secondary', mb: 1 }}
          >
            Connection: {formatPrice(raw)}
          </Typography>
        );
      }
      case 'available_technology_types': {
        const techList = parseListText(j ?? plan.available_technology_types ?? undefined);
        if (techList.length === 0) return null;
        return (
          <Box key="available_technology_types" sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
              <Typography variant="body2" component="span">
                {techList.join(', ')}
              </Typography>
            </Box>
          </Box>
        );
      }
      case 'typical_evening_speed': {
        const text = j ?? plan.typical_evening_speed;
        if (!text) return null;
        return (
          <Box key="typical_evening_speed" sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {text}
            </Typography>
          </Box>
        );
      }
      case 'customer_type': {
        const raw = j ?? plan.customer_type;
        if (!raw || String(raw).trim() === '') return null;
        const text = String(raw).trim();
        const pretty = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        return (
          <Box key="customer_type" sx={{ mt: 0.5, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pretty}
            </Typography>
          </Box>
        );
      }
      case 'description': {
        const descriptionList = parseListText(j ?? plan.description);
        if (descriptionList.length === 0) return null;
        return (
          <Box key="description" sx={{ mt: 0.5, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {label}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'none' }}>
              {descriptionList.map((itemText, i) => (
                <Box
                  key={i}
                  component="li"
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.25, '&:last-child': { mb: 0 } }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.15, flexShrink: 0 }} />
                  <Typography variant="body2">{itemText}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      }
      case 'features': {
        const featuresList = parseListText(j ?? plan.features);
        if (featuresList.length === 0) return null;
        return (
          <Box key="features" sx={{ mt: 0.5, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {label}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'none' }}>
              {featuresList.map((feature, i) => (
                <Box
                  key={i}
                  component="li"
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.25, '&:last-child': { mb: 0 } }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.15, flexShrink: 0 }} />
                  <Typography variant="body2">{feature}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      }
      case 'one_time_price': {
        const js = j;
        const raw = js != null && js.trim() !== '' ? parseFloat(js) : plan.one_time_price;
        if (raw == null || Number(raw) <= 0) return null;
        return (
          <Typography key="one_time_price" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {label}: {formatPrice(raw)}
          </Typography>
        );
      }
      case 'contract_terms': {
        const text = j ?? plan.contract_terms;
        if (!text) return null;
        return (
          <Typography key="contract_terms" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {text}
          </Typography>
        );
      }
      case 'max_channels': {
        const text = j ?? (plan.max_channels != null ? String(plan.max_channels) : undefined);
        if (text == null || text === '') return null;
        return (
          <Typography key="max_channels" variant="body2" sx={{ mb: 1 }}>
            {label}: {text}
          </Typography>
        );
      }
      default: {
        if (j == null) return null;
        const parts = parseListText(j);
        if (parts.length > 1) {
          return (
            <Box key={fieldKey} sx={{ mt: 0.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {label}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'none' }}>
                {parts.map((line, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.25, '&:last-child': { mb: 0 } }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.15, flexShrink: 0 }} />
                    <Typography variant="body2">{line}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }
        return (
          <Box key={fieldKey} sx={{ mt: 0.5, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {j}
            </Typography>
          </Box>
        );
      }
    }
  };

  return (
    <Box>
      {!hideHeading && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {subcategoryName
            ? `Select your ${subcategoryName} plan`
            : 'Select your plan'}
        </Typography>
      )}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <IconButton
          onClick={() => scroll('left')}
          sx={{
            position: 'absolute',
            left: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: 'grey.200',
            width: 40,
            height: 40,
            '&:hover': { bgcolor: 'grey.300' },
            '&.Mui-disabled': { bgcolor: 'grey.100' },
          }}
          disabled={plans.length === 0}
          size="small"
          aria-label="Scroll plans left"
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => scroll('right')}
          sx={{
            position: 'absolute',
            right: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: 'grey.200',
            width: 40,
            height: 40,
            '&:hover': { bgcolor: 'grey.300' },
            '&.Mui-disabled': { bgcolor: 'grey.100' },
          }}
          disabled={plans.length === 0}
          size="small"
          aria-label="Scroll plans right"
        >
          <ChevronRightIcon />
        </IconButton>

        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
            pt: 0.5,
            px: 0.5,
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            '& > *': { scrollSnapAlign: 'start', flexShrink: 0 },
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { borderRadius: 4, bgcolor: 'action.hover' },
          }}
        >
          {plans.map((plan) => {
            const isSelected = selectedPlan?.id === plan.id;
            return (
              <Card
                key={plan.id}
                variant="outlined"
                sx={{
                  width: CARD_WIDTH,
                  minWidth: CARD_WIDTH,
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  boxShadow: isSelected ? 4 : 0,
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {headerFieldKeys.length > 0 && (
                  <Box
                    sx={{
                      bgcolor: ORDER_BAR_BG,
                      color: ORDER_BAR_TEXT,
                      px: 2,
                      pt: 2,
                      pb: 1.5,
                      borderRadius: 0,
                    }}
                  >
                    {headerFieldKeys.map((key) => renderField(key, plan, true))}
                  </Box>
                )}
                <CardContent sx={{ pb: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {bodyFieldKeys.map((key) => renderField(key, plan))}
                </CardContent>
                <Box sx={{ px: 2, py: 2 }}>
                  {getOrderLink ? (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="medium"
                      component={Link}
                      href={getOrderLink(plan)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 600 }}
                    >
                      Order this plan
                      <OpenInNewIcon sx={{ fontSize: 18, ml: 0.5 }} />
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant={isSelected ? 'contained' : 'outlined'}
                      color="primary"
                      size="medium"
                      onClick={() => onSelectPlan(plan)}
                      sx={{ fontWeight: 600 }}
                    >
                      {isSelected ? 'Selected' : 'Select plan'}
                    </Button>
                  )}
                  {plan.critical_info_summary_path && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                      <Link
                        href={`${(process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '')}/api/orders/cis/${plan.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.875rem',
                        }}
                      >
                        Critical Information Summary
                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                      </Link>
                    </Box>
                  )}
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default PlanCarousel;
